import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import ContactsAddContactPage from "@/Shared/Contacts/Pages/ContactsAddContactPage";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import { LocationsWizardBody } from "../CustomComponents/Internal/locationsWizardBody";
import LocationsAddLocationDialogAddressPage from "../Pages/LocationsAddLocationDialogAddressPage";
import LocationsAddLocationDialogDetailsPage from "../Pages/LocationsAddLocationDialogDetailsPage";
import LocationsAddLocationDialogOtherPage from "../Pages/LocationsAddLocationDialogOtherPage";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { LocationsSavingBehaviourType } from "../Types/LocationsSavingBehaviourType";

export interface LocationsAddResult {
  location: SavedLocation;
  shouldSaveToAddressBook: boolean;
}

interface LocationsAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: LocationContext;
  savingBehaviour: LocationsSavingBehaviourType;
  onFinished: (result: LocationsAddResult) => Promise<void>;
}

const addLocationPageDescription = {
  details: "Please tell us about the location you are adding",
  address: "Please enter the remaining parts of the address",
  contact: "Please enter contact details for this location",
  other: "Please enter other details",
};

// Four-page add-location wizard (details -> address -> contact -> other),
// hosted in a ui-core Dialog. The pages render their own navigation buttons,
// so the dialog supplies no footer. With the "Ask" saving behaviour, a
// stacked confirm dialog asks whether to save to the address book.
export function LocationsAddDialog(props: LocationsAddDialogProps) {
  const [location, setLocation] = useState<AddLocationLocationType>({
    locationType: undefined,
    businessName: undefined,
    address: {},
    accessorials: [],
    latitudeLongitude: undefined,
    notes: "",
    hours: {
      openFrom: "10:00",
      openUntil: "16:00",
    },
    distributionWarehouseBrand: undefined,
    locationHasDock: LoadingCapabilitiesAvailability.Unknown,
    locationHasForklift: LoadingCapabilitiesAvailability.Unknown,
  });

  const [contact, setContact] = useState<AddContactContactType>({});

  const [currentPage, setCurrentPage] = useState<
    "details" | "address" | "contact" | "other"
  >("details");

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const saveConfirmResolverRef = useRef<
    ((shouldSave: boolean) => void) | undefined
  >(undefined);

  // With the "Ask" behaviour: resolves the user's yes/no from the stacked
  // confirm dialog; otherwise always saves.
  function shouldSaveToAddressBook(): Promise<boolean> {
    if (props.savingBehaviour !== LocationsSavingBehaviourType.Ask) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      saveConfirmResolverRef.current = resolve;
      setSaveConfirmOpen(true);
    });
  }

  function settleSaveConfirm(shouldSave: boolean) {
    saveConfirmResolverRef.current?.(shouldSave);
    saveConfirmResolverRef.current = undefined;
  }

  async function onDone() {
    const shouldSave = await shouldSaveToAddressBook();

    const locationId = uuidv4();
    const contactId = uuidv4();

    await props.onFinished({
      shouldSaveToAddressBook: shouldSave,
      location: {
        savedLocationId: locationId,
        location: location as Location,
        contacts: [
          {
            savedContactId: contactId,
            savedLocationId: locationId,
            contact: contact as Contact,
          },
        ],
        defaultSavedContactId: contactId,
        isFavourite: true,
        isBranch: false,
        ...(location.locationHasDock !== undefined
          ? { locationHasDock: location.locationHasDock }
          : {}),

        ...(location.locationHasForklift !== undefined
          ? { locationHasForklift: location.locationHasForklift }
          : {}),
      },
    });
    props.onOpenChange(false);
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Add Location"
        // Each page's intro line belongs to the dialog header, under the title
        // it qualifies — inside the body it read as a caption for whichever
        // field happened to come first.
        description={addLocationPageDescription[currentPage]}
        width="wide"
      >
        <ControlPresentationProvider presentation="underlined">
          {/* Consistent page width: the Other page is naturally 550px, and the
           * address grid needs an explicit width to not shrink-wrap. */}
          <LocationsWizardBody testId="add-location-modal">
            {currentPage === "details" && (
              <LocationsAddLocationDialogDetailsPage
                onSwitchToAddressPage={() => setCurrentPage("address")}
                setLocation={setLocation}
                location={location}
                onCancel={() => props.onOpenChange(false)}
                context={props.context}
              />
            )}

            {currentPage === "address" && (
              <LocationsAddLocationDialogAddressPage
                setLocation={setLocation}
                location={location}
                onPrevious={() => setCurrentPage("details")}
                onSwitchToContactPage={() => setCurrentPage("contact")}
              />
            )}

            {currentPage === "contact" && (
              <ContactsAddContactPage
                contactChanged={function (
                  updatedContact: AddContactContactType,
                ) {
                  setContact({
                    ...contact,
                    ...updatedContact,
                  });
                }}
                contact={contact}
                onPrevious={() => setCurrentPage("address")}
                onDone={() => setCurrentPage("other")}
              />
            )}

            {currentPage === "other" && (
              <LocationsAddLocationDialogOtherPage
                setLocation={setLocation}
                location={location}
                onPrevious={() => setCurrentPage("contact")}
                onDone={onDone}
              />
            )}
          </LocationsWizardBody>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={saveConfirmOpen}
        onOpenChange={function (nowOpen) {
          setSaveConfirmOpen(nowOpen);
          if (!nowOpen) {
            settleSaveConfirm(false);
          }
        }}
        title="Do you want save this location to your address book?"
        okText="Save Location"
        cancelText="No"
        onOk={function () {
          settleSaveConfirm(true);
          return DialogResult.Close;
        }}
      >
        <ControlPresentationProvider presentation="underlined">
          If you save it to your address book, this location will be easily
          available for future shipments. Otherwise, this location will just be
          used for this one shipment.
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
